import { useEffect } from 'react'
import { resolveSite } from '@/lib/site'
import { localizeTerms, siteHasTermSwaps } from '@/lib/terminology'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'

/**
 * Applies per-site institution terminology (terminology.ts) to all rendered
 * text. Mounted once in __root.
 *
 * Works at the DOM text-node level instead of per-string call sites because
 * learner copy is spread across copy files, inline slide TSX, chapters.ts and
 * scams/index.ts — a walker covers all of it (and future copy) from one
 * integration point. React re-renders restore source text; the observer
 * re-applies in the same microtask, before paint, so swapped terms never
 * flash. Writes only happen when the text actually changes, so our own
 * mutations can't loop the observer.
 */

const SKIPPED_PARENTS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA'])

export function useTerminology() {
  useEffect(() => {
    const site = resolveSite()
    if (!siteHasTermSwaps(site)) return
    // A community partner's members bank at banks; keep the source wording.
    if (COMMUNITY_PARTNER?.terminology === 'bank') return

    const applyToTextNode = (node: Text) => {
      const parent = node.parentElement
      if (parent && SKIPPED_PARENTS.has(parent.tagName)) return
      const swapped = localizeTerms(node.data, site)
      if (swapped !== node.data) node.data = swapped
    }

    const applyToTree = (root: Node) => {
      if (root.nodeType === Node.TEXT_NODE) {
        applyToTextNode(root as Text)
        return
      }
      if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let current = walker.nextNode()
      while (current) {
        applyToTextNode(current as Text)
        current = walker.nextNode()
      }
    }

    applyToTree(document.body)

    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.type === 'characterData') {
          applyToTextNode(m.target as Text)
        } else {
          m.addedNodes.forEach(applyToTree)
        }
      }
    })
    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])
}
